import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

import { BACKEND_ENDPOINTS } from '@/constant/network';
import {
  EXPIRES_AT_SESSION_BUFFER_SEC,
  OIDC_AUTH_STATUES,
  OIDC_CHECK_INTERVAL_MS,
  OIDC_QUERY_PARAMS,
} from '@/constant/oidc';
import { getGlobalArgs } from '@/root/global';
import { oidcApiV1 } from '@/services/http';
import { OIDCClientLoginState } from '@/types/oidc';
import { equals } from '@/utils/common';
import { handleSilentRenew, isExpiredSession, isSessionValid } from '@/utils/oidc';
import { getFullCurrentPath } from '@/utils/routes';

export const useOIDCAuth = (shouldSkip?: boolean, checkSessionInterval = OIDC_CHECK_INTERVAL_MS) => {
  const {
    isAuthenticated,
    user,
    isLoading,
    signinRedirect,
    signoutRedirect,
    settings,
    signinSilent,
    removeUser,
    clearStaleState,
    revokeTokens,
  } = useAuth();

  const expires_at = user?.expires_at;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const code = searchParams.get(OIDC_QUERY_PARAMS.code);

  useEffect(() => {
    if (Boolean(code)) {
      const params = new URLSearchParams(searchParams);
      params.delete(OIDC_QUERY_PARAMS.code);
      params.delete(OIDC_QUERY_PARAMS.state);
      return router.replace(`${pathname}${params.toString() ? `?${params}` : ''}`);
    }
    oidcApiV1.get<OIDCClientLoginState>({ url: BACKEND_ENDPOINTS.oidcLoginState }).then(async ({ data }) => {
      if (equals(data.state, OIDC_AUTH_STATUES.authorized)) {
        if (!isAuthenticated) {
          return signinRedirect({ redirect_uri: getFullCurrentPath(window.location) });
        }

        return;
      }

      await removeUser();
      await revokeTokens();
      await clearStaleState();

      if (!shouldSkip) {
        return signinRedirect({ redirect_uri: getFullCurrentPath(window.location) });
      }
      return;
    });
  }, []);

  useEffect(() => {
    if (shouldSkip || isLoading || isAuthenticated) return;
    signinRedirect({ redirect_uri: getFullCurrentPath(window.location) });
  }, [isAuthenticated, isLoading, shouldSkip]);

  useEffect(() => {
    if (!settings.automaticSilentRenew || !isAuthenticated) return;

    const checkAndRenewSession = () => {
      if (isExpiredSession(expires_at, EXPIRES_AT_SESSION_BUFFER_SEC)) {
        if (shouldSkip) return handleSilentRenew(signinSilent);
        return handleSilentRenew(signinSilent, signoutRedirect);
      }
    };

    const interval = setInterval(checkAndRenewSession, checkSessionInterval);
    return () => clearInterval(interval);
  }, [isAuthenticated, settings.automaticSilentRenew, expires_at, shouldSkip]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      const isValidSession = await isSessionValid(expires_at, EXPIRES_AT_SESSION_BUFFER_SEC);
      if (!isValidSession) {
        if (shouldSkip) {
          await removeUser();
          await revokeTokens();
          return await clearStaleState();
        }
        return signoutRedirect({
          post_logout_redirect_uri: getGlobalArgs('NEXT_PUBLIC_OIDC_REDIRECT_URI'),
        });
      }
    }, checkSessionInterval);

    return () => clearInterval(interval);
  }, [shouldSkip, isAuthenticated, expires_at, checkSessionInterval]);

  return {
    isLoading,
    isAuthenticated,
    user,
  };
};
